import { AccessControl } from '@components/layout'
import {
  useCreateUserNote,
  useDeleteUserNote,
  UserNote,
  useUpdateUserNote,
  useUserNotes
} from '@services/users/mutations/useUserNotes'
import dayjs from 'dayjs'
import { Pencil, Plus, Search, StickyNote, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

interface UserNotesSectionProps {
  userId: string
}

export function UserNotesSection({ userId }: UserNotesSectionProps) {
  const { t } = useTranslation('users')
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editingNote, setEditingNote] = useState<UserNote | null>(null)
  const [content, setContent] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { data: notes, isLoading } = useUserNotes(userId, search)
  const { mutate: createNote, isPending: isCreating } = useCreateUserNote()
  const { mutate: updateNote, isPending: isUpdating } = useUpdateUserNote()
  const { mutate: deleteNote } = useDeleteUserNote()

  const openAdd = () => {
    setEditingNote(null)
    setContent('')
    setError(null)
    setModalOpen(true)
  }

  const openEdit = (note: UserNote) => {
    setEditingNote(note)
    setContent(note.content)
    setError(null)
    setModalOpen(true)
  }

  const handleDeleteNote = (noteId: string) => {
    if (window.confirm(t('notes.deleteConfirm'))) {
      deleteNote({ userId, noteId })
    }
  }

  const close = () => {
    setModalOpen(false)
    setEditingNote(null)
    setContent('')
    setError(null)
  }

  const handleSubmit = () => {
    if (!content.trim()) {
      setError(t('form.validation.required'))
      return
    }
    if (editingNote) {
      updateNote(
        { userId, noteId: editingNote.id, content },
        { onSuccess: close }
      )
    } else {
      createNote({ userId, content }, { onSuccess: close })
    }
  }

  return (
    <AccessControl role="ADMIN">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="m-0 flex items-center gap-2.5 font-display text-xl font-bold tracking-tight text-ink">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <StickyNote className="h-5 w-5" />
            </span>
            {t('notes.title')}
          </h2>
          <Button onClick={openAdd}>
            <Plus className="h-4 w-4" />
            {t('notes.addNote')}
          </Button>
        </div>

        <div className="relative max-w-sm">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('notes.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[0, 1].map((i) => (
              <div
                key={i}
                className="h-20 animate-pulse rounded-2xl bg-ink/[0.04]"
              />
            ))}
          </div>
        ) : !notes || notes.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-ink/15 px-6 py-10 text-center text-sm text-muted-foreground">
            {t('notes.noNotes')}
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <div
                key={note.id}
                className="rounded-2xl border border-ink/[0.06] bg-white p-4 shadow-[0_6px_18px_rgba(4,33,37,0.04)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="m-0 whitespace-pre-wrap text-sm leading-relaxed text-ink/85">
                    {note.content}
                  </p>
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      onClick={() => openEdit(note)}
                      className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink/40 transition-colors hover:bg-ink/[0.05] hover:text-ink"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteNote(note.id)}
                      className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink/40 transition-colors hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex justify-between gap-3 text-xs text-muted-foreground">
                  <span>
                    {note.created_by_name || t('notes.unknownAuthor')}
                  </span>
                  <span>
                    {dayjs(note.created_at).format('DD.MM.YYYY HH:mm')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={modalOpen} onOpenChange={(o) => !o && close()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingNote ? t('notes.editNote') : t('notes.addNote')}
              </DialogTitle>
            </DialogHeader>
            <div>
              <Textarea
                rows={4}
                value={content}
                autoFocus
                onChange={(e) => {
                  setContent(e.target.value)
                  setError(null)
                }}
                placeholder={t('notes.contentPlaceholder')}
              />
              {error && (
                <p className="mt-1.5 text-sm font-medium text-destructive">
                  {error}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghostDark" onClick={close}>
                {t('notes.cancel')}
              </Button>
              <Button onClick={handleSubmit} disabled={isCreating || isUpdating}>
                {t('notes.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AccessControl>
  )
}
